import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import LazyImageArticleCard from '@/components/articles/LazyImageArticleCard';
import { News } from '@/types';
import { Loader2 } from 'lucide-react';

interface InfiniteNewsGridProps {
  data: any; // Infinite query data
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
  error: any;
  className?: string;
  // Layout configuration - more descriptive and flexible
  layout?: 'single' | 'double' | 'triple' | 'quad'; // Describes number of columns
  cardVariant?: 'hero' | 'row' | 'compact' | 'sidebar'; // Card appearance variant
  gap?: 'gap-4' | 'gap-6' | 'gap-8';
  emptyMessage?: string;
  loadingMessage?: string;
  errorMessage?: string;
  showLoadMore?: boolean;
  autoLoad?: boolean;
  threshold?: number;
  // Backward compatibility
  gridCols?: 'grid-cols-1' | 'grid-cols-2' | 'grid-cols-3' | 'grid-cols-4';
}

const InfiniteNewsGrid: React.FC<InfiniteNewsGridProps> = ({
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  isError,
  error,
  className = '',
  layout = 'double',
  cardVariant = 'compact',
  gap = 'gap-6',
  emptyMessage = 'Tidak ada berita yang ditemukan.',
  loadingMessage = 'Memuat berita...',
  errorMessage = 'Gagal memuat berita. Silakan coba lagi.',
  showLoadMore = false,
  autoLoad = true,
  threshold = 0.1,
  // Backward compatibility
  gridCols,
}) => {
  // Intersection Observer for auto-loading
  const { ref: loadMoreRef, inView } = useInView({
    threshold,
    triggerOnce: false,
  });

  // Convert layout to grid classes with backward compatibility
  const getGridClasses = useMemo(() => {
    // If gridCols is provided (backward compatibility), use it
    if (gridCols) {
      return gridCols;
    }

    // Otherwise use the new layout system
    switch (layout) {
      case 'single':
        return 'grid-cols-1';
      case 'double':
        return 'md:grid-cols-2';
      case 'triple':
        return 'md:grid-cols-3';
      case 'quad':
        return 'md:grid-cols-4';
      default:
        return 'md:grid-cols-2';
    }
  }, [layout, gridCols]);   

  // Auto-load when in view
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && autoLoad) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, autoLoad]);

  // Flatten all pages data
  const allNews = useMemo(() => {
    if (!data?.pages) return [];
    
    return data.pages.reduce((acc: News[], page: any) => {
      if (page?.data && Array.isArray(page.data)) {
        return [...acc, ...page.data];
      }
      return acc;
    }, []);
  }, [data?.pages]);

  // Remove duplicates based on ID
  const uniqueNews = useMemo(() => {
    const seen = new Set();
    return allNews.filter((news: News) => {
      if (seen.has(news.id)) {
        return false;
      }
      seen.add(news.id);
      return true;
    });
  }, [allNews]);

  // Loading state
  if (isLoading) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-red-600" />
            <p className="text-gray-600 dark:text-gray-400">{loadingMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{errorMessage}</p>
            <button
              onClick={() => fetchNextPage()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Coba Lagi
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state - return null to hide the component when no content
  if (uniqueNews.length === 0) {
    return null;
  }

  return (
    <div className={`${className}`}>
      {/* News Grid */}
      <div className={`grid ${getGridClasses} ${gap} mb-8`}>
        {uniqueNews.map((news: News, index: number) => (
          <LazyImageArticleCard
            key={`news-${news.id}-${index}`}
            news={news}
            variant={cardVariant}
            priorityImage={index < 4} // Prioritize first 4 images
            className="h-full"
          />
        ))}
      </div>

      {/* Load More Section */}
      {(hasNextPage || isFetchingNextPage) && (
        <div ref={loadMoreRef} className="flex items-center justify-center py-8">
          {isFetchingNextPage ? (
            <div className="text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-red-600" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Memuat berita lainnya...
              </p>
            </div>
          ) : showLoadMore ? (
            <button
              onClick={() => fetchNextPage()}
              className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Muat Berita Lainnya
            </button>
          ) : (
            <div className="h-4" /> // Invisible trigger for auto-load
          )}
        </div>
      )}

      {/* End indicator */}
      {!hasNextPage && uniqueNews.length > 0 && (
        <div className="text-center py-8">
          <div className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400">
            <div className="h-px bg-gray-300 dark:bg-gray-600 w-12"></div>
            <span className="text-sm">Semua berita telah dimuat</span>
            <div className="h-px bg-gray-300 dark:bg-gray-600 w-12"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfiniteNewsGrid;
